import {
  GRAPH_TAG_RULE_OPTIONS,
  getRowsFromGraphTagsFileContent,
} from "@features/administration/products/EditProduct/graphTagsHelper"
import { MetadataFormSectionCard } from "@features/builder/UpdateMetadata/MetadataFormSectionCard"
import { METADATA_FORM_SECTIONS } from "@features/builder/UpdateMetadata/helpers"
import { TagValuesAutocomplete } from "@features/builder/UpdateMetadata/TagValuesAutocomplete"
import { type MetadataFormValues } from "@features/builder/UpdateMetadata/validation"
import { Delete, HelpOutline } from "@mui/icons-material"
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material"
import { useGetProductGraphTagsQuery } from "@store/services/productsService"
import { useEffect, useMemo } from "react"
import { Controller, useFieldArray, useFormContext, useWatch } from "react-hook-form"

export const MetadataFormTagsSection = () => {
  const form = useFormContext<MetadataFormValues>()
  const { control } = form
  const { fields, append, remove } = useFieldArray({ control, name: "tags" })

  const productKeyValue = useWatch({ control, name: "product_key" })
  const [orgKey, divKey, prodKey] = productKeyValue.split("#")

  const { data: tagsResponse, isFetching } = useGetProductGraphTagsQuery(
    { key: prodKey ?? "", orgKey: orgKey ?? "", divKey: divKey ?? "" },
    { skip: !prodKey || !orgKey || !divKey },
  )

  const tagDefinitions = useMemo(
    () => getRowsFromGraphTagsFileContent(tagsResponse ?? ""),
    [tagsResponse],
  )
  const productTagNames = useMemo(
    () => new Set(tagDefinitions.map((definition) => definition.Name)),
    [tagDefinitions],
  )

  // list every tag the product defines, seeding rows for ones not yet saved on this graph
  useEffect(() => {
    tagDefinitions.forEach((definition) => {
      const alreadyExists = form.getValues("tags")?.some((tag) => tag.Name === definition.Name)
      if (alreadyExists) return

      append({
        Name: definition.Name,
        Rule: definition.Rule,
        Description: definition.Description,
        Values: [],
      })
    })
  }, [tagDefinitions, append, form])

  return (
    <MetadataFormSectionCard id={METADATA_FORM_SECTIONS.tags} title="Tags">
      {isFetching ? (
        <Stack alignItems="center">
          <CircularProgress size={24} />
        </Stack>
      ) : (
        <Stack direction="column" spacing={2}>
          {fields.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No graph tags have been defined for this product.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Name</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Rule</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Description</strong>
                  </TableCell>
                  <TableCell sx={{ minWidth: 300 }}>
                    <strong>Values</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Actions</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fields.map((field, index) => {
                  const ruleDescription = GRAPH_TAG_RULE_OPTIONS.find(
                    (option) => option.value === field.Rule,
                  )?.description
                  // tags saved on the graph might have been removed from the product since, keep showing them
                  const isOrphaned = !productTagNames.has(field.Name)

                  return (
                    <TableRow key={field.id}>
                      <TableCell>{field.Name}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <span>{field.Rule}</span>
                          {ruleDescription && (
                            <Tooltip
                              title={
                                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                  {ruleDescription.map((rule) => (
                                    <li key={rule}>{rule}</li>
                                  ))}
                                </Box>
                              }
                            >
                              <HelpOutline fontSize="small" sx={{ cursor: "pointer" }} />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{field.Description}</TableCell>
                      <TableCell sx={{ minWidth: 300 }}>
                        <Controller
                          control={control}
                          name={`tags.${index}.Values`}
                          render={({ field: valuesField }) => (
                            <TagValuesAutocomplete
                              value={valuesField.value ?? []}
                              onChange={valuesField.onChange}
                            />
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        {isOrphaned && (
                          <Tooltip title="Remove tag">
                            <IconButton size="small" color="error" onClick={() => remove(index)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </Stack>
      )}
    </MetadataFormSectionCard>
  )
}
